import {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
  useRef,
} from "react";
import { useAuth } from "../context/AuthContext";
import { apiRequest } from "../config/api";
import toast from "react-hot-toast";

const getApiErrorMessage = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || err?.message || fallback;

const CartContext = createContext();

const GUEST_CART_KEY = "guest_cart";
const CART_COUPON_KEY = "cart_coupon";

const getStoredCouponCode = () => {
  try {
    return localStorage.getItem(CART_COUPON_KEY) || "";
  } catch {
    return "";
  }
};

const setStoredCouponCode = (code) => {
  try {
    if (code) localStorage.setItem(CART_COUPON_KEY, code);
    else localStorage.removeItem(CART_COUPON_KEY);
  } catch {
    /* ignore */
  }
};

// Helper functions for guest cart
const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    if (!cart) return [];
    const parsed = JSON.parse(cart);
    if (Array.isArray(parsed)) return parsed;
    return parsed.items || [];
  } catch (error) {
    console.error("Error reading guest cart:", error);
    return [];
  }
};

const setGuestCart = (cart) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error("Error saving guest cart:", error);
  }
};

const clearGuestCart = () => {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
  } catch (error) {
    console.error("Error clearing guest cart:", error);
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [couponCode, setCouponCodeState] = useState(getStoredCouponCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const migrateInFlightRef = useRef(false);

  // Optimized fetch cart items with better error handling
  const fetchCart = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Authenticated user - fetch from API
        const response = await apiRequest("/cart", {
          skipGlobalErrorToast: true,
        });
        setCart(
          response.data && response.data.items && response.data.summary
            ? response.data
            : {
                items: [],
                summary: { subtotal: 0, total_items: 0, total_unique_items: 0 },
              }
        );
      } else {
        // Guest user - get from localStorage
        const guestCart = getGuestCart();
        if (guestCart.length > 0) {
          // For guest users, use the items in localStorage directly (they have variant details)
          const cartItems = guestCart.filter(
            (item) => item.variant && item.variant_id
          );
          const subtotal = cartItems.reduce((sum, item) => {
            const price =
              item.variant.price_sale || item.variant.price_regular || 0;
            return sum + price * item.quantity;
          }, 0);
          const total_items = cartItems.reduce(
            (sum, item) => sum + item.quantity,
            0
          );
          setCart({
            items: cartItems,
            summary: {
              subtotal: subtotal,
              total_items: total_items,
              total_unique_items: cartItems.length,
            },
          });
        } else {
          setCart({
            items: [],
            summary: { subtotal: 0, total_items: 0, total_unique_items: 0 },
          });
        }
      }
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to fetch cart");
      setError(message);
      console.error("Cart fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add item to cart
  const addToCart = useCallback(
    async (variantId, quantity = 1) => {
      setLoading(true);
      setError(null);

      try {
        if (user) {
          // Authenticated user - save to database
          const response = await apiRequest("/cart", {
            method: "POST",
            skipGlobalErrorToast: true,
            data: {
              variant_id: variantId,
              quantity,
            },
          });

          // Refresh cart after adding
          await fetchCart();
          return response.data;
        } else {
          // Guest user - POST to /api/cart to get variant details
          const response = await apiRequest("/cart", {
            method: "POST",
            skipGlobalErrorToast: true,
            data: { variant_id: variantId, quantity },
          });
          if (response.success && response.data) {
            const guestCart = getGuestCart();
            const existingItemIndex = guestCart.findIndex(
              (item) => item.variant_id === variantId
            );
            const availableStock = response.data.variant?.stock ?? 0;
            if (existingItemIndex >= 0) {
              const nextQty =
                guestCart[existingItemIndex].quantity + quantity;
              if (nextQty > availableStock) {
                throw new Error(
                  `Only ${availableStock} items available in stock`
                );
              }
              guestCart[existingItemIndex].quantity = nextQty;
              guestCart[existingItemIndex].variant = response.data.variant;
            } else {
              if (quantity > availableStock) {
                throw new Error(
                  `Only ${availableStock} items available in stock`
                );
              }
              guestCart.push(response.data);
            }
            setGuestCart(guestCart);
            await fetchCart();
            return {
              success: true,
              message: "Item added to guest cart",
              data: response.data,
            };
          } else {
            throw new Error(
              response.message || "Failed to add item to guest cart"
            );
          }
        }
      } catch (err) {
        setError(err.message || "Failed to add item to cart");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user, fetchCart]
  );

  // Update cart item quantity
  const updateCartItem = useCallback(
    async (itemId, quantity) => {
      setLoading(true);
      setError(null);

      try {
        if (user) {
          // Authenticated user - update in database
          const response = await apiRequest(`/cart/${itemId}`, {
            method: "PUT",
            skipGlobalErrorToast: true,
            data: { quantity },
          });

          // Refresh cart after updating
          await fetchCart();
          return response.data;
        } else {
          // Guest user - update in localStorage
          const guestCart = getGuestCart();
          const itemIndex = guestCart.findIndex((item) => item.id === itemId);

          if (itemIndex >= 0) {
            if (quantity <= 0) {
              // Remove item if quantity is 0 or negative
              guestCart.splice(itemIndex, 1);
            } else {
              // Update quantity
              guestCart[itemIndex].quantity = quantity;
            }

            // Save to localStorage
            setGuestCart(guestCart);

            // Refresh cart to get updated data
            await fetchCart();

            return {
              success: true,
              message:
                quantity <= 0
                  ? "Item removed from guest cart"
                  : "Guest cart updated",
              data: quantity <= 0 ? null : guestCart[itemIndex],
            };
          }
        }
      } catch (err) {
        setError(err.message || "Failed to update cart item");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user, fetchCart]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    async (itemId) => {
      setLoading(true);
      setError(null);

      try {
        if (user) {
          // Authenticated user - remove from database
          await apiRequest(`/cart/${itemId}`, {
            method: "DELETE",
            skipGlobalErrorToast: true,
          });

          // Refresh cart after removing
          await fetchCart();
        } else {
          // Guest user - remove from localStorage
          const guestCart = getGuestCart();
          const updatedCart = guestCart.filter((item) => item.id !== itemId);
          setGuestCart(updatedCart);

          // Refresh cart to get updated data
          await fetchCart();
        }
      } catch (err) {
        setError(err.message || "Failed to remove item from cart");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user, fetchCart]
  );

  // Clear entire cart
  const clearCart = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        await apiRequest("/cart", {
          method: "DELETE",
          skipGlobalErrorToast: true,
        });
        setStoredCouponCode("");
        setCouponCodeState("");
        await fetchCart();
      } else {
        // Guest user - clear from localStorage
        clearGuestCart();
        setStoredCouponCode("");
        setCouponCodeState("");
        setCart({
          items: [],
          summary: { subtotal: 0, total_items: 0, total_unique_items: 0 },
        });
      }
    } catch (err) {
      setError(err.message || "Failed to clear cart");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, fetchCart]);

  // Migrate guest cart to user account (runs once per login session)
  const migrateGuestCart = useCallback(async () => {
    if (!user?.id || migrateInFlightRef.current) return false;

    const migrationKey = `guest_cart_migrated_${user.id}`;
    if (sessionStorage.getItem(migrationKey) === "done") return false;

    const guestCart = getGuestCart();
    if (guestCart.length === 0) {
      sessionStorage.setItem(migrationKey, "done");
      return false;
    }

    migrateInFlightRef.current = true;
    const itemsToMigrate = [...guestCart];
    clearGuestCart();

    try {
      for (const item of itemsToMigrate) {
        await apiRequest("/cart", {
          method: "POST",
          skipGlobalErrorToast: true,
          data: {
            variant_id: item.variant_id,
            quantity: item.quantity,
          },
        });
      }

      sessionStorage.setItem(migrationKey, "done");
      return true;
    } catch (error) {
      setGuestCart(itemsToMigrate);
      console.error("Error migrating guest cart:", error);
      toast.error("Some cart items could not be saved to your account");
      return false;
    } finally {
      migrateInFlightRef.current = false;
    }
  }, [user]);

  // Load cart when user changes; migrate guest cart first on login
  useEffect(() => {
    let cancelled = false;

    const loadCart = async () => {
      if (user) {
        await migrateGuestCart();
      }
      if (!cancelled) {
        await fetchCart();
      }
    };

    loadCart();

    return () => {
      cancelled = true;
    };
  }, [user, migrateGuestCart, fetchCart]);

  const setCouponCode = useCallback((code) => {
    const normalized = (code || "").trim().toUpperCase();
    setStoredCouponCode(normalized);
    setCouponCodeState(normalized);
  }, []);

  const clearCouponCode = useCallback(() => {
    setStoredCouponCode("");
    setCouponCodeState("");
  }, []);

  const value = {
    cart,
    couponCode,
    setCouponCode,
    clearCouponCode,
    loading,
    error,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refetch: fetchCart,
    isGuest: !user,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
